import { ReactNode, useMemo } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { TFunction } from 'i18next';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ErrorDisplay } from '@/components/ui/error-display';
import { Input } from '@/components/ui/input';
import { FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from '@/components/ui/form';
import { StoreNameInput } from './store-name-input';
import { PresentmentCurrenciesSelect } from './presentment-currencies-select';
import { ChargeCurrencySelect } from './charge-currency-select';
import { BaseCurrencySelect } from './base-currency-select';
import { MultipleSubscriptionsSwitch } from './multiple-subscriptions-switch';
import { ApplyExternalBXGYDiscountOnSubscriptionSwitch } from './apply-external-bxgy-discount-on-subscription-switch';
import { DigitalProductSwitch } from './digital-product-switch';
import { CreateDemoProductsSwitch } from './create-demo-products-switch';
import { CustomThankYouUrlInput } from './custom-thank-you-url-input';
import { StoreLogoInput } from './store-logo-input';
import { StorePrimaryColorInput } from './store-primary-color-input';
import { CryptoRescueBox } from './crypto-rescue-box';

const HEX_COLOR_REGEX = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

const createStoreFormSchema = (t: TFunction) =>
  z.object({
    name: z.string().min(1, t('validation.nameRequired')),
    presentmentCurrencies: z.array(z.string()).min(1, t('validation.presentmentCurrenciesRequired')),
    chargeCurrency: z.string().min(1, t('validation.chargeCurrencyRequired')),
    baseCurrency: z.string().min(1, t('validation.baseCurrencyRequired')),
    multipleSubscriptionsPerCustomerEnabled: z.boolean(),
    createDemoProducts: z.boolean().optional(),
    customThankYouUrl: z.string().url(t('validation.invalidUrl')).optional().or(z.literal('')),
    primaryColor: z
      .string()
      .regex(HEX_COLOR_REGEX, t('validation.invalidHexColor'))
      .optional()
      .or(z.literal('')),
    logoUrl: z.string().optional(),
    applyExternalBXGYDiscountOnSubscription: z.boolean(),
    digitalProduct: z.boolean(),
    currencyRateOverrides: z.string().optional(),
  });

export type StoreFormData = z.infer<ReturnType<typeof createStoreFormSchema>>;

interface StoreFormProps {
  defaultValues: StoreFormData;
  currencies: string[];
  onSubmit: (data: StoreFormData) => Promise<void>;
  formError: { message: string; isValidationError: boolean } | null;
  onDismissError: () => void;
  header: ReactNode;
  basicInformationTitle: string;
  currenciesTitle: string;
  settingsTitle: string;
  brandingTitle?: string;
  baseCurrencyEditable: boolean;
  showDemoSeeding?: boolean;
  dangerZone?: ReactNode;
}

/**
 * Store form component
 * Reusable form for creating or editing stores
 */
export const StoreForm = ({
  defaultValues,
  currencies,
  onSubmit,
  formError,
  onDismissError,
  header,
  basicInformationTitle,
  currenciesTitle,
  settingsTitle,
  brandingTitle,
  baseCurrencyEditable,
  showDemoSeeding = false,
  dangerZone,
}: StoreFormProps) => {
  const { t } = useTranslation('stores');
  const resolvedBrandingTitle = brandingTitle ?? t('branding.title');
  const storeFormSchema = useMemo(() => createStoreFormSchema(t), [t]);
  const methods = useForm<StoreFormData>({
    defaultValues,
    resolver: zodResolver(storeFormSchema),
    mode: 'onBlur',
  });

  const { handleSubmit } = methods;

  return (
    <div className="bg-background">
      <div className="flex flex-col space-y-4 sm:space-y-6">
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {header}

            {formError && <ErrorDisplay error={formError} onDismiss={onDismissError} />}

            <div className="mt-6 grid gap-6 lg:grid-cols-3">
              <div className="space-y-6 lg:col-span-2">
                <Card>
                  <CardHeader>
                    <CardTitle>{basicInformationTitle}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <StoreNameInput />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>{resolvedBrandingTitle}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <StoreLogoInput />
                    <StorePrimaryColorInput />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>{currenciesTitle}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <PresentmentCurrenciesSelect currencies={currencies} />
                    <ChargeCurrencySelect currencies={currencies} />
                    <BaseCurrencySelect currencies={currencies} editable={baseCurrencyEditable} />
                    <FormField
                      name="currencyRateOverrides"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('form.currencyRateOverrides')}</FormLabel>
                          <FormControl>
                            <Input placeholder={t('form.currencyRateOverridesPlaceholder')} {...field} />
                          </FormControl>
                          <FormDescription>
                            {t('form.currencyRateOverridesDescription')}
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>{settingsTitle}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <MultipleSubscriptionsSwitch />
                    <ApplyExternalBXGYDiscountOnSubscriptionSwitch />
                    <DigitalProductSwitch />
                    {showDemoSeeding ? <CreateDemoProductsSwitch /> : null}
                    <CustomThankYouUrlInput />
                  </CardContent>
                </Card>
                <CryptoRescueBox />
                {dangerZone}
              </div>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};
